import { Typography } from '@mui/material';
import PageTitle from '../../../../components/Accessibility/PageTitle';
import DefaultButton from '../../../../components/Accessibility/DefaultButton';
import VoltarButton from '../../../../components/Accessibility/VoltarButton';
import NumberedTabs from '../../../../components/NumberedTabs';
import LoadingApp from '../../../../components/LoadingApp';
import ModalFeedBack from '../../../../components/ModalFeedBack';
import ModalAction from '../../../../components/ModalAction';
import { useAccessibility } from '../../../../Store/contexts/AccessibilityContext';
import TermosUsoModal from '../components/TermosUsoModal';
import { TranscricaoProvider } from './context';
import { useNovaTranscricaoPage } from './hooks/useNovaTranscricaoPage';
import { useRascunhoAtivo } from './hooks/useRascunhoAtivo';
import { transcricaoSteps } from './steps';
import { TranscricaoStepEnum } from './types';
import StepAnexos from './steps/StepAnexos';
import StepTranscricao from './steps/StepTranscricao';
import './styles.css';

const NovaTranscricaoContent: React.FC = () => {
  const accessibility = useAccessibility();
  const {
    state,
    termosModalAberto,
    setTermosModalAberto,
    salvandoTranscricao,
    entregandoTranscricao,
    showModalFeedBack,
    setShowModalFeedBack,
    showModalSucesso,
    titleModal,
    subTitleModal,
    canProceed,
    nextStep,
    previousStep,
    podeVoltar,
    handleTermosConfirmados,
    handleSolicitarAutorizacao,
    handleEntendiSucesso,
    exibirSolicitarAutorizacao,
    solicitarAutorizacaoDisabled,
  } = useNovaTranscricaoPage();

  const {
    verificandoRascunhoAtivo,
    showModalRascunhoAtivo,
    handleRecusarRascunhoAtivo,
    handleContinuarRascunhoAtivo,
  } = useRascunhoAtivo();

  const bloqueadoPorRascunho = verificandoRascunhoAtivo || showModalRascunhoAtivo;

  const renderStep = () => {
    switch (state.currentStep) {
      case TranscricaoStepEnum.Anexos:
        return <StepAnexos />;
      case TranscricaoStepEnum.Transcricao:
        return <StepTranscricao />;
      default:
        return null;
    }
  };

  return (
    <div className="nova-transcricao-container">
      <PageTitle text="Nova transcrição" className="nova-transcricao-title" />

      <Typography className="transcricao-fora-cobertura-mensagem" style={accessibility.textStyleAccessibility}>
        Envie pedidos prescritos por médicos e profissionais não credenciados da Unimed Campinas.
      </Typography>

      <div className="transcricao-tabs-holder">
        <NumberedTabs steps={transcricaoSteps} currentStep={state.currentStep} />
      </div>

      <div className="nova-transcricao-step">{renderStep()}</div>

      {(state.currentStep !== TranscricaoStepEnum.Transcricao || exibirSolicitarAutorizacao) && (
        <div className="nova-transcricao-actions">
          {exibirSolicitarAutorizacao ? (
            <DefaultButton
              text="SOLICITAR AUTORIZAÇÃO"
              onClick={handleSolicitarAutorizacao}
              disabled={solicitarAutorizacaoDisabled}
            />
          ) : (
            <DefaultButton
              text="PROSSEGUIR"
              onClick={() => nextStep(bloqueadoPorRascunho)}
              disabled={!canProceed() || bloqueadoPorRascunho}
            />
          )}
          {podeVoltar && <VoltarButton onClick={previousStep} />}
        </div>
      )}

      <TermosUsoModal
        open={termosModalAberto}
        onClose={() => {
          if (!salvandoTranscricao) {
            setTermosModalAberto(false);
          }
        }}
        onConfirm={handleTermosConfirmados}
        isSubmitting={salvandoTranscricao}
      />

      <ModalFeedBack
        showModal={showModalFeedBack}
        handleShowModal={() => setShowModalFeedBack(false)}
        icon="error"
        textButton="Fechar"
        title={titleModal}
        subTitle={subTitleModal}
        showButtonModal
        handleButtonModal={() => setShowModalFeedBack(false)}
        classNameModal="modal-feedback-error"
      />

      <ModalFeedBack
        showModal={showModalSucesso}
        handleShowModal={handleEntendiSucesso}
        icon="warning"
        textButton="ENTENDI"
        title="Transcrição em análise."
        subTitle={(
          <>
            Sua solicitação está em análise por nossa equipe. Para acompanhar o andamento, acesse a
            opção{' '}
            <strong className="transcricao-auditoria-destaque">
              &quot;Minhas Transcrições&quot;
            </strong>
            .
          </>
        )}
        showButtonModal
        handleButtonModal={handleEntendiSucesso}
      />

      <ModalAction
        showModal={showModalRascunhoAtivo}
        handleShowModal={handleRecusarRascunhoAtivo}
        icon="warning"
        title="Transcrição em andamento"
        subTitle="Já existe uma transcrição em andamento. Deseja continuar?"
        textOutlinedButton="Não, iniciar nova"
        textContainedButton="Sim, continuar"
        handleOutlinedButton={handleRecusarRascunhoAtivo}
        handleContainedButton={handleContinuarRascunhoAtivo}
      />

      <LoadingApp
        showLoading={verificandoRascunhoAtivo || entregandoTranscricao}
        text={
          entregandoTranscricao
            ? 'Enviando solicitação...'
            : 'Verificando transcrição em andamento...'
        }
      />
    </div>
  );
};

const NovaTranscricao: React.FC = () => (
  <TranscricaoProvider>
    <NovaTranscricaoContent />
  </TranscricaoProvider>
);

export default NovaTranscricao;
